import { useState, useContext } from "react";
import styles from "./styles.module.css";
import ShopContext from "context/ShopContext";
import { roundOff } from "helpers/NumberFormatHelper";
import { PAYMENT_MODES } from "const";

function BalanceDialog({ balance, paymentModes, onSave, onClose }) {
  const { selectedShop } = useContext(ShopContext);

  const [localPayments, setLocalPayments] = useState(paymentModes.map((m) => ({ ...m, amount: 0 })));
  const [activeMode, setActiveMode] = useState(() => {
    const cashMode = paymentModes.find((m) => m.name && m.name.toLowerCase() === PAYMENT_MODES.CASH);
    return cashMode ? cashMode.name : paymentModes[0]?.name || "";
  });

  const handleKeypadInput = (val) => {
    setLocalPayments((prev) =>
      prev.map((mode) => {
        if (mode.name === activeMode) {
          let newAmount = mode.amount;

          if (val === "clear") {
            newAmount = 0;
          } else if (val === "Delete") {
            newAmount = Number(newAmount.toString().slice(0, -1) || "0");
          } else if (val.startsWith("+")) {
            const increment = Number(val.replace("+", ""));
            newAmount = (Number(newAmount) || 0) + increment;
          } else if (!isNaN(val) && val.length === 1) {
            newAmount = Number(`${newAmount}${val}`);
          }

          return { ...mode, amount: newAmount };
        }
        return mode;
      }),
    );
  };

  const currentPayment = localPayments.reduce((sum, m) => sum + m.amount, 0);
  const finalPayment = balance - currentPayment;

  return (
    <div className={styles.popUp}>
      <div className={styles.balanceWrapper}>
        <div className={styles.payBalance}>
          <h3>Pay balance</h3>
        </div>
        {/* Header */}
        <div className={styles.headerRow}>
          <div className={styles.headerContent}>
            <h5>Current Balance</h5>
            <h2>
              <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
              {roundOff(balance)}
            </h2>
          </div>
          <div className={styles.headerContent}>
            <h5>Current Payment</h5>
            <h2>
              <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
              {roundOff(currentPayment)}
            </h2>
          </div>
          <div className={styles.headerContent}>
            <h5>Final Payment</h5>
            <h2>
              <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
              {roundOff(finalPayment)}
            </h2>
          </div>
        </div>

        {/* Payment Tabs */}
        <div className={styles.paymentTabs}>
          {localPayments.map((method, index) => (
            <button
              key={method.name}
              onClick={() => setActiveMode(method.name)}
              className={`${index === 0 ? styles.firstBtn : ""} ${
                index === localPayments.length - 1 ? styles.lastBtn : ""
              } ${method.name === activeMode ? styles.activeTab : ""}`}
            >
              <div className={styles.paymentHead}>
                {method.icon && <img src={method.icon} alt={method.name} className={styles.paymentIcon} />}
                <h3 className={styles.label}>{method.name}</h3>
              </div>
              <h2 className={styles.amount}>
                <div className={styles.rupeeIcon}>{selectedShop?.currency_symbol}</div>
                {roundOff(method.amount)}
              </h2>
            </button>
          ))}
        </div>

        {/* Keypad */}
        <div className={styles.keypad}>
          {/* Row 1 */}
          <button
            className={styles.keyBtn}
            style={{ borderTopLeftRadius: "20px" }}
            onClick={() => handleKeypadInput("1")}
          >
            1
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("2")}>
            2
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("3")}>
            3
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("+10")}>
            +10
          </button>
          <button
            className={styles.keyBtn}
            style={{ borderTopRightRadius: "20px" }}
            onClick={() => handleKeypadInput("+100")}
          >
            +100
          </button>

          {/* Row 2 */}
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("4")}>
            4
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("5")}>
            5
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("6")}>
            6
          </button>
          <button className={`${styles.keyBtn} ${styles.numeric}`} onClick={() => handleKeypadInput("+50")}>
            +50
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("+200")}>
            +200
          </button>

          {/* Row 3 */}
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("7")}>
            7
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("8")}>
            8
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("9")}>
            9
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("+/-")}>
            +/-
          </button>
          <button className={`${styles.keyBtn} ${styles.numeric}`} onClick={() => handleKeypadInput("+500")}>
            +500
          </button>

          {/* Row 4 */}
          <button
            className={styles.keyBtn}
            style={{ borderBottomLeftRadius: "20px" }}
            onClick={() => handleKeypadInput("clear")}
          >
            Clear
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("0")}>
            0
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput(".")}>
            .
          </button>
          <button className={styles.keyBtn} onClick={() => handleKeypadInput("Delete")}>
            Delete
          </button>
          <button
            className={`${styles.keyBtn} ${styles.numeric}`}
            style={{ borderBottomRightRadius: "20px" }}
            onClick={() => handleKeypadInput(`+${finalPayment}`)}
          >
            +{roundOff(finalPayment)}
          </button>
        </div>

        {/* Footer */}
        <div className={styles.footerBtns}>
          <button className={styles.closeBtn} onClick={onClose} style={{ fontSize: "16px" }}>
            Cancel
          </button>
          <button
            className={styles.printBtn}
            onClick={() => onSave(localPayments)}
            disabled={finalPayment !== 0}
            style={{ fontSize: "16px" }}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
}

export default BalanceDialog;
